import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Lock, ShieldAlert } from 'lucide-react';
interface Props {
  onUnlocked: () => void;
}

export default function AdminLockScreen({ onUnlocked }: Props) {
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secret.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('admin-toggle-lock', {
        body: { action: 'unlock', secret: secret.trim() },
      });
      if (fnErr || (data && data.ok === false) || (data && data.error)) {
        const code = data?.error;
        if (code === 'locked_out') {
          setError(`Locked out — ${data?.max ?? 5} attempts exhausted. Manual reset required via Telegram.`);
        } else if (code === 'invalid secret') {
          const remaining = typeof data?.remaining === 'number' ? data.remaining : null;
          setError(remaining !== null ? `Invalid secret code (${remaining} attempts left)` : 'Invalid secret code');
        } else {
          setError(code || fnErr?.message || 'Unlock failed');
        }
        setLoading(false);
        return;
      }
      onUnlocked();
    } catch (err: any) {
      setError(err?.message || 'Unlock failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'hsl(var(--bg-app))' }}>
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl p-8 border"
          style={{
            background: 'hsl(var(--bg-card))',
            borderColor: 'hsl(var(--border) / 0.4)',
            boxShadow: '0 20px 60px -10px rgba(0,0,0,0.6)',
          }}
        >
          <div className="flex flex-col items-center mb-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'hsl(var(--destructive) / 0.15)', border: '1px solid hsl(var(--destructive) / 0.3)' }}
            >
              <ShieldAlert size={28} style={{ color: 'hsl(var(--destructive))' }} />
            </div>
            <h1 className="text-xl font-light text-ivory tracking-wide">Admin Panel Locked</h1>
            <p className="text-sm text-ivory/50 mt-2 text-center">
              Emergency lock active. Enter the lock secret to restore access.
            </p>
          </div>

          <form onSubmit={handleUnlock} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-ivory/40 mb-2 block">
                Secret Code
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory/30" />
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  disabled={loading}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm text-ivory outline-none"
                  style={{
                    background: 'hsl(var(--bg-app))',
                    border: '1px solid hsl(var(--border) / 0.4)',
                  }}
                />
              </div>
            </div>

            {error && (
              <div
                className="text-xs px-3 py-2 rounded-md"
                style={{ background: 'hsl(var(--destructive) / 0.1)', color: 'hsl(var(--destructive))' }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !secret.trim()}
              className="w-full py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
              style={{
                background: 'linear-gradient(135deg, #d4af37, #b8941f)',
                color: '#0a0a0a',
              }}
            >
              {loading ? 'Verifying…' : 'Unlock Admin Panel'}
            </button>

            <button
              type="button"
              onClick={() => supabase.auth.signOut()}
              className="w-full text-xs text-ivory/40 hover:text-ivory/70 transition-colors pt-2"
            >
              Sign out
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
